// src/components/DashboardTabs/BasicTab.jsx
import { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { api } from "../../services/api";
import ProfileImageUploader from "../ProfileImageUploader/ProfileImageUploader.jsx";
import { pickMunicipalityFromComponents, resolveMunicipality } from "../../utils/municipality.js";

// Sallitut esiintyjätyypit – pidä linjassa SetupWizardBase.jsx:n kanssa
const ARTIST_TYPES = [
  "Bilebändi",
  "Trubaduuri",
  "Duo",
  "DJ",
  "Sooloartisti",
  // "Taikuri",
];
const COMMISSION_BY_TYPE = { Trubaduuri: 12, Sooloartisti: 15 };
const DEFAULT_COMMISSION_PCT = 15;

// Yrittää johtaa kunnan bändin datasta (location -> gmaps-komponentit -> osoitestring)
const deriveMunicipality = (bandObj = {}) => {
  // 1) Jos location-kenttä on jo tunnistettavissa, normalisoidaan se
  const current = resolveMunicipality(bandObj.location || "");
  if (current) return current;

  // 2) Yritetään erilaisista paikoista, mihin wizard on voinut tallettaa GMaps-komponentit
  const comps =
    bandObj?.google_place?.address_components ||
    bandObj?.address_components ||
    bandObj?.address?.address_components ||
    [];

  const fromComps = pickMunicipalityFromComponents(comps);
  if (fromComps) return fromComps;

  // 3) Viimeinen oljenkorsi: koko osoiterivi tekstinä (aliaset, postinumerot ym.)
  const addrText =
    bandObj.band_address ||
    bandObj?.address?.formatted_address ||
    bandObj?.google_place?.formatted_address ||
    bandObj.address ||
    "";

  const fromText = resolveMunicipality(addrText);
  if (fromText) return fromText;

  return "";
};

// Profiilikuvan URL normalisointi samaan originiin
const ORIGIN_FROM_API = (() => {
  try {
    const base = typeof window !== "undefined" ? window.location.origin : "https://localhost";
    return new URL(process.env.REACT_APP_API_BASE ?? base, base).origin;
  } catch { return typeof window !== "undefined" ? window.location.origin : ""; }
})();
const normalizeProfileForSave = (v) => {
  if (!v) return v;
  try {
    const u = new URL(v, ORIGIN_FROM_API);
    return u.origin === ORIGIN_FROM_API ? `${u.pathname}${u.search}${u.hash}` : v;
  } catch { return v; }
};

// ----- Helpers moved to module scope -----
const toStringArray = (v) => {
  if (v == null) return [];
  if (Array.isArray(v)) {
    return v
      .map((x) => {
        if (typeof x === "string") return x;
        if (typeof x === "number") return String(x);
        return (
          x?.name ?? x?.label ?? x?.instrument ?? x?.service ?? x?.title ?? x?.id ?? ""
        );
      })
      .map(String)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return String(v)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

const mapNamesToIds = (names, dictionary) => {
  const byName = new Map(
    dictionary.map((d) => [String(d.name ?? d.label).toLowerCase(), String(d.id)])
  );
  const outIds = [];
  const unknown = [];
  for (const raw of names) {
    const name = String(raw).trim();
    if (!name) continue;
    const id = byName.get(name.toLowerCase());
    if (id) outIds.push(id);
    else unknown.push(name);
  }
  return {
    ids: Array.from(new Set(outIds)),
    unknown: Array.from(new Set(unknown)),
  };
};

const normalizeFieldFromBand = (raw, dictionary) => {
  const dictIds = new Set(dictionary.map((d) => String(d.id)));
  const asArray = toStringArray(raw);
  const looksLikeAllIds =
    asArray.length > 0 && asArray.every((v) => dictIds.has(String(v)));
  if (looksLikeAllIds)
    return { ids: Array.from(new Set(asArray.map(String))), unknown: [] };
  return mapNamesToIds(asArray, dictionary);
};

export default function BasicTab({
  band,
  onSaved,
  onProfileChange,
  canEdit = true,
  exposeSave,
}) {
  const [form, setForm] = useState({
    name: band?.name || "",
    slug: band?.slug || "",
    artist_type_name: band?.artist_type || "",
    location: band?.location || "",
    email: band?.band_email || "",
    phone: band?.band_phone || "",
    address: band?.band_address || "", // lukittu
    description_short: band?.description ?? band?.description_short ?? "",
    profile_image: (band?.profile_image || band?.profile_image_url || "").trim(),

  });

  // Välityspalkkioprosentti UI:ssa (helppo laajentaa COMISSION_BY_TYPE-mapilla)
  const commissionPct = useMemo(() => {
    if (!form.artist_type_name) return null;
    return COMMISSION_BY_TYPE[form.artist_type_name] ?? DEFAULT_COMMISSION_PCT;
  }, [form.artist_type_name]);

  // ---------- Genret ----------
  const [allGenres, setAllGenres] = useState([]); // [{ id, name }]
  const [genreIds, setGenreIds] = useState([]); // ["id", ...] (max 6)
  const [genreOpen, setGenreOpen] = useState(false);

  // ---------- Sanastot & valinnat (Soittimet + Palvelut) ----------
  const [dicts, setDicts] = useState({ instruments: [], services: [] });
  const [dictLoading, setDictLoading] = useState(true);
  const [dictError, setDictError] = useState("");
  const [instrumentIds, setInstrumentIds] = useState([]); // string[]
  const [serviceIds, setServiceIds] = useState([]); // string[]
  const [saving, setSaving] = useState(false);
  // avaa/sulje listat (ei enää tarvita Ctrl/Cmd-ohjetta)
  const [instrumentsOpen, setInstrumentsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  // lisää/poista id lista-tilasta (rajaus soittimille)
  const toggleId = (list, setList, id, checked, max = Infinity) => {
    const sid = String(id);
    setList(prev => {
      const has = prev.includes(sid);
      if (checked) {
        if (has) return prev;
        if (prev.length >= max) return prev;
        return [...prev, sid];
      }
      return prev.filter(x => x !== sid);
    });
  };

  const rawInstrFallback = useMemo(
    () => toStringArray(band?.instruments ?? band?.instrument_names ?? band?.instrument_list),
    [band?.instruments, band?.instrument_names, band?.instrument_list]
  );

  // Aluechippien lähde: pricing -> region_name
  const pricedRegions = useMemo(() => {
    const pr = Array.isArray(band?.pricing) ? band.pricing : [];
    const names = Array.from(
      new Set(
        pr
          .filter((p) => (p.base_price ?? p.price) != null)
          .map((p) => (p.region_name ?? p.region ?? "").trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b, "fi"));
    if (names.length) return names;

    const rr = band?.performance_regions || band?.performance_regions_string || [];
    return Array.isArray(rr)
      ? rr
      : String(rr)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
  }, [band?.pricing, band?.performance_regions, band?.performance_regions_string]);

  const MAX_DESC = 1000;
  const MAX_INSTRUMENTS = 8;
  const MAX_SERVICES = 6;

  // Päivitä lomake kun bändi vaihtuu
  useEffect(() => {
    if (!band) return;

    const canonLoc = resolveMunicipality(band.location || "");
    const autoLoc = canonLoc || deriveMunicipality(band);

    setForm({
      name: band.name ?? "",
      slug: band.slug ?? "",
      artist_type_name: band.artist_type ?? "",
      location: autoLoc || "",
      email: band.band_email ?? "",
      phone: band.band_phone ?? "",
      address: band.band_address ?? "", // lukittu
      description_short: band.description ?? band.description_short ?? "",
      profile_image: (band.profile_image || band.profile_image_url || "").trim(),
    });
  }, [band]);

  // Esitäytä / päivitä paikkakunta osoitedatasta, jos sitä ei ole tai se muuttuu
    useEffect(() => {
      if (!band) return;
      const autoLoc = deriveMunicipality(band);
      if (autoLoc && autoLoc !== form.location) {
        setForm(f => ({ ...f, location: autoLoc }));
      }
      // HUOM: mukana vain datakentät, jotka oikeasti vaikuttavat johdettuun arvoon
    }, [
      band,
      band?.band_address,
      band?.address,
      band?.address_components,
      band?.google_place,
      band?.google_place?.formatted_address,
      form.location
    ]);

  // ---------- Genres: lataus + normalisointi ----------
  const normalizeGenres = (raw) => {
    const list = raw?.genres ?? raw?.items ?? raw?.data ?? raw ?? [];
    if (!Array.isArray(list)) return [];
    return list
      .map((g) => {
        const name = String(
          typeof g === "string" ? "" : g.name ?? g.genre ?? g.label ?? ""
        ).trim();
        const idRaw = typeof g === "object" ? g.id ?? g.genre_id ?? g.value : null;
        const id = idRaw != null ? String(idRaw) : null;
        return name && id ? { id, name } : null;
      })
      .filter(Boolean);
  };

  useEffect(() => {
    (async () => {
      try {
        const raw = await api.genres();
        setAllGenres(normalizeGenres(raw));
      } catch (e) {
        console.error("genres load fail", e);
        setAllGenres([]);
      }
    })();
  }, []);

useEffect(() => {
  if (!band?.id) return;

  let ab = new AbortController();

  // 1) onko propsista data kokonaan poissa?
  const propsMissing =
    band?.instruments == null && band?.additional_services == null;

  // 2) onko state vielä tyhjä?
  const nothingSelected =
    instrumentIds.length === 0 && serviceIds.length === 0;

  // 3) voimmeko ylipäätään mapata (sanastot ladattu)?
  const readyForLookup =
    !dictLoading && (dicts.instruments.length > 0 || dicts.services.length > 0);

  // Jos propsit puuttuvat TAI state on tyhjä ja sanastot valmiit -> hae tuore bändi
  if (!readyForLookup || (!propsMissing && !nothingSelected)) return;

  (async () => {
    try {
      const fresh = await api.bandByIdOrSlug(band.id, { signal: ab.signal });
      const b = fresh?.band ?? fresh ?? {};
      const ins = normalizeFieldFromBand(b.instruments ?? "", dicts.instruments);
      const srv = normalizeFieldFromBand(b.additional_services ?? "", dicts.services);

      setInstrumentIds(ins.ids);
      setServiceIds(srv.ids);
    } catch (e) {
      console.warn("Hydration failed", e);
    }
  })();

  return () => ab.abort();
}, [
  band?.id,
  band?.instruments,
  band?.additional_services,
  dictLoading,
  dicts.instruments,
  dicts.services,
  instrumentIds.length,
  serviceIds.length,
]);



  useEffect(() => {
    if (!allGenres.length) return;

    if (Array.isArray(band?.genre_ids) && band.genre_ids.length) {
      setGenreIds(band.genre_ids.map(String).slice(0, 6));
      return;
    }
    const names = Array.isArray(band?.genres)
      ? band.genres
          .map((g) => (typeof g === "string" ? g : g.name ?? g.genre ?? ""))
          .map((s) => String(s).trim())
          .filter(Boolean)
      : [];
    if (!names.length) {
      setGenreIds([]);
      return;
    }

    const byName = new Map(allGenres.map((g) => [g.name.toLowerCase(), String(g.id)]));
    const ids = names
      .map((n) => byName.get(n.toLowerCase()))
      .filter((id) => id != null)
      .slice(0, 6);
    setGenreIds(ids);
  }, [band?.genre_ids, band?.genres, allGenres]);

  const toggleGenre = (id, checked) => {
    const sid = String(id);
    setGenreIds((prev) => {
      const has = prev.some((x) => String(x) === sid);
      if (checked) return has || prev.length >= 6 ? prev : [...prev, sid];
      return prev.filter((x) => String(x) !== sid);
    });
  };
  const removeGenre = (id) => toggleGenre(id, false);

  // Hae instruments & services sanastot ja normalisoi bändin arvot
  useEffect(() => {
    let alive = true;
    (async () => {
      setDictLoading(true);
      setDictError("");
      try {
        const d =
          (await api.bands?.dictionaries?.()) ??
          (await api.dictionaries?.()); // fallback

        const instruments = Array.isArray(d?.instruments) ? d.instruments : [];
        const services = Array.isArray(d?.services) ? d.services : [];

        if (!alive) return;
        setDicts({ instruments, services });

        const ins = normalizeFieldFromBand(band?.instruments ?? "", instruments);
        const srv = normalizeFieldFromBand(band?.additional_services ?? "", services);

        setInstrumentIds(ins.ids);
        setServiceIds(srv.ids);
      } catch (e) {
        console.error("Dictionaries load failed", e);
        if (alive) {
          setDicts({ instruments: [], services: [] });
          setDictError("Vaihtoehtojen haku epäonnistui.");
        }
      } finally {
        if (alive) setDictLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [band?.instruments, band?.additional_services, band?.id]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({
      ...f,
      [name]: name === "description_short" ? value.slice(0, MAX_DESC) : value,
    }));
  };

  // ---- TALLENNUS ----
  const doSave = useCallback(
    async (e) => {
      e.preventDefault();
      if (!canEdit || saving) return;
        if (!String(form.profile_image || "").trim()) {
        toast.error("Lisää profiilikuva ennen julkaisua (voit tallentaa muut kentät nyt).", { containerId: "global" });
      }

      const emailOk = /^\S+@\S+\.\S+$/.test((form.email || "").trim());
      if (!emailOk) {
        toast.error("Anna voimassa oleva sähköposti.", { containerId: "global" });
        return;
      }
      if (!String(form.phone || "").trim()) {
        toast.error("Puhelinnumero on pakollinen.", { containerId: "global" });
        return;
      }

      const canonicalNumericIds = genreIds
        .map((sel) => allGenres.find((g) => String(g.id) === String(sel))?.id)
        .filter((id) => /^\d+$/.test(String(id)))
        .map((id) => Number(id))
        .slice(0, 6);

      if (!canonicalNumericIds.length && genreIds.length) {
        toast.error(
          "Genre-ID:t puuttuvat backendiltä – päivitä sivu ja yritä uudelleen.",
          { containerId: "global" }
        );
        return;
      }

      setSaving(true);
      try {
        // Ota lomakkeesta kaikki paitsi name, slug ja address (lukittuja)
        const { name, slug, address, ...rest } = form;
        const desc = String(rest.description_short || "").trim();   
        // *** Lähetä vain backendin tuntemat kentät ***
        const payload = {
          // suorat
          location: (rest.location ?? "").trim(),
          profile_image: normalizeProfileForSave(rest.profile_image),
          // aliakset -> API-kentät
          band_email: (rest.email ?? "").trim(),
          band_phone: (rest.phone ?? "").trim(),
          // tekstialue mapataan backend-kenttään "description"
          description: (rest.description_short ?? "").trim(),
          // artist type oikealla nimellä (DB: artist_type)
          // Backend odottaa kenttää artist_type_name (updateBandCore)
          artist_type_name: rest.artist_type_name,
        };

        // Varmista että emme vahingossa lähetä description_short:ia
        // (jos form spreadaisi jotain myöhemmin tms.)
        delete payload.description_short;

        // Lähetä valitut ID:t sellaisenaan (tunnistamattomien mappailu poistettu)
        payload.instruments = instrumentIds;        // [] sallittu
        payload.additional_services = serviceIds;   // [] sallittu

        // HUOM: EI band_addressia tänne, koska kenttä on read-only tässä välilehdessä

        await api.updateBand(band.id, payload);

        // Genret erillisellä reitillä
        await api.saveBandGenres?.(band.id, canonicalNumericIds);

        const fresh = await api.bandByIdOrSlug(band.id);

        
        toast.success("Perustiedot tallennettu.", { containerId: "global" });

        // heijasta heti lomakkeeseen
        setForm((f) => ({ ...f, description_short: desc }));

        if (canonicalNumericIds.length) setGenreIds(canonicalNumericIds);
        onSaved?.(fresh || { ...payload, genre_ids: canonicalNumericIds });
      } catch (err) {
        console.error(err);
        toast.error("Tallennus epäonnistui.", { containerId: "global" });
      } finally {
        setSaving(false);
      }
    },
    [
      canEdit,
      saving,
      form,
      band?.id,
      genreIds,
      allGenres,
      onSaved,
      instrumentIds,
      serviceIds,
    ]
  );

  useEffect(() => {
    if (typeof exposeSave === "function")
      exposeSave(() => doSave({ preventDefault: () => {} }));
    return () => {
      if (typeof exposeSave === "function") exposeSave(null);
    };
  }, [exposeSave, doSave]);

const label =
  "block dark:text-white/90 text-black/90 font-customFont2 text-lg mb-1 font-bold";
const input =
  "w-full h-10 rounded-lg px-3 bg-white dark:bg-black/40 border border-black/10 dark:border-white/10 mb-3 flex items-center gap-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-xs md:text-xs";

const disabled = !canEdit || saving;

/* ---- Neon-pill napit ---- */
const pillBase = [
  "group relative inline-flex items-center gap-1.5 sm:gap-2",
  // mobiili pienempi, isommilla normaali
  "rounded-full px-2 py-0.5 sm:px-3 sm:py-1.5",
  "text-[11px] sm:text-sm",                     // ⬅️ koko pienemmäksi mobiilissa
  "font-normal text-shadow-sm tracking-wide",
  "transition duration-300 ease-out",
  "focus:outline-none focus:ring-2 focus:ring-pink/60",
  "after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:opacity-0",
  "after:bg-shimmer after:bg-[length:700px_100%] group-hover:after:opacity-40 group-hover:after:animate-shimmer",
].join(" ");

const pillSelected =
  "text-white bg-pink ring-1 ring-pink shadow-neon-pink before:opacity-60 before:blur-[8px]";

const pillGhost =
  "text-black/80 dark:text-white/90 bg-white/80 dark:bg-white/10 ring-1 ring-black/10 dark:ring-white/15 hover:bg-white dark:hover:bg-white/15 before:opacity-0 before:blur-[6px] hover:before:opacity-40";

const chipCloseBtn = [
  "ml-1 inline-flex items-center justify-center rounded-full",
  "h-3.5 w-3.5 sm:h-4 sm:w-4 text-[10px] sm:text-xs leading-none",
  "bg-black/20 dark:bg-white/20 hover:bg-black/30 dark:hover:bg-white/30",
].join(" ");


const pillActive =
  [
    "bg-pink text-white shadow",
    "hover:brightness-110 hover:-translate-y-[1px] hover:shadow-neon-pink",
    "active:translate-y-0.5"
  ].join(" ");


  return (
  <form
    onSubmit={doSave}
    className="grid grid-cols-1
              sm:grid-cols-12 md:grid-cols-12 mdd:grid-cols-12
              lg:grid-cols-12 xl:grid-cols-12 2xl:grid-cols-12
              gap-4 p-4"
  >

    {/* Nimi (lukittu) */}
    <div className="col-span-12 sm:col-span-12 md:col-span-6 mdd:col-span-6 lg:col-span-6 xl:col-span-6 2xl:col-span-6">
      <label className={label}>Esiintyjän nimi</label>
      <input
        className={input}
        name="name"
        value={form.name}
        onChange={onChange}
        readOnly
        required
        aria-readonly="true"
      />
      <p className="mt-1 text-xs opacity-70">
        Nimeä voi muuttaa vain ylläpidon kautta.{" "}
        <a href="/ota-yhteytta" className="underline">Ota yhteyttä</a>.
      </p>
    </div>

    {/* Profiilikuva */}
    <div className="col-span-12 sm:col-span-12 md:col-span-6 mdd:col-span-6 lg:col-span-6 xl:col-span-6 2xl:col-span-6">
      <label className={label}>Profiilikuva</label>
        <ProfileImageUploader
          bandId={band.id}
          value={form.profile_image}
          onChange={(url) => {
            if (disabled) return;
            setForm(f => ({ ...f, profile_image: url }));
            onProfileChange?.(url); // päivittää myös Dashboardin oikean reunan kuvan
          }}
          disabled={disabled}
        />

    </div>

    {/* Esiintyjätyyppi */}
    <div className="col-span-12 sm:col-span-12 md:col-span-6 mdd:col-span-6 lg:col-span-6 xl:col-span-6 2xl:col-span-6">
      <label className={label}>Esiintyjätyyppi</label>
      <select
        className={input}
        name="artist_type_name"
        value={ARTIST_TYPES.includes(form.artist_type_name) ? form.artist_type_name : ""}
        onChange={onChange}
        disabled={disabled}
        required
      >
        <option value="" disabled>Valitse tyyppi…</option>
        {ARTIST_TYPES.map((t) => (
          <option key={t} value={t}>{t}</option>
        ))}
      </select>
      <p className="mt-1 text-xs opacity-70">
        {form.artist_type_name
          ? `Buukkerin välityspalkkio: ${commissionPct} %`
          : "Välityspalkkio näkyy, kun valitset esiintyjätyypin."}
      </p>
    </div>

    {/* Slug (lukittu) */}
    <div className="col-span-12 sm:col-span-12 md:col-span-6 mdd:col-span-6 lg:col-span-6 xl:col-span-6 2xl:col-span-6">
      <label className={label}>Slug</label>
      <input
        className={input}
        name="slug"
        value={form.slug}
        onChange={onChange}
        readOnly
        required
        aria-readonly="true"
      />
      <p className="mt-1 text-xs opacity-70">
        Slugia ei voi muuttaa hallinnassa. Tarvitsetko muutoksen?{" "}
        <a href="/otayhteytta" className="underline">Ota yhteyttä</a>.
      </p>
    </div>

    {/* Sähköposti */}
    <div className="col-span-12 sm:col-span-12 md:col-span-6 mdd:col-span-6 lg:col-span-6 xl:col-span-6 2xl:col-span-6">
      <label className={label}>Sähköposti</label>
      <input
        className={input}
        name="email"
        type="email"
        value={form.email}
        onChange={onChange}
        disabled={disabled}
        required
      />
    </div>

    {/* Puhelin */}
    <div className="col-span-12 sm:col-span-12 md:col-span-6 mdd:col-span-6 lg:col-span-6 xl:col-span-6 2xl:col-span-6">
      <label className={label}>Puhelinnumero</label>
      <input
        className={input}
        name="phone"
        value={form.phone}
        onChange={onChange}
        disabled={disabled}
        required
      />
    </div>

    {/* Osoite (lukittu) */}
    <div className="col-span-12 sm:col-span-12 md:col-span-6 mdd:col-span-6 lg:col-span-6 xl:col-span-6 2xl:col-span-6">
      <label className={label}>Osoite</label>
      <input
        className={input}
        name="address"
        value={form.address}
        readOnly
        aria-readonly="true"
        autoComplete="street-address"
      />
      <p className="mt-1 text-xs opacity-70">
        Osoite asetetaan rekisteröinnissä. Muutoksista ole yhteydessä ylläpitoon.
      </p>
    </div>

    {/* Paikkakunta (johdettu osoitteesta) */}
    <div className="col-span-12 sm:col-span-12 md:col-span-6 mdd:col-span-6 lg:col-span-6 xl:col-span-6 2xl:col-span-6">
      <label className={label}>Paikkakunta</label>
      <input
        className={input}
        name="location"
        value={form.location}
        readOnly
        aria-readonly="true"
      />
      <p className="mt-1 text-xs opacity-70">
        Paikkakunta haetaan automaattisesti osoitteesta.
      </p>
    </div>

    {/* Väli-viiva */}
    <div className="col-span-12">
      <div
        role="separator"
        aria-hidden="true"
        className="
          w-full max-w-full my-8 h-[2px] rounded-full
          bg-gradient-to-r from-primary/50 via-pink/50 to-electric/50
          dark:from-primary dark:via-pink dark:to-electric
          ring-1 ring-black/5 dark:ring-white/10
        "
      />
    </div>

    {/* Kuvaus */}
    <div className="col-span-12 sm:col-span-12 md:col-span-12 mdd:col-span-12 lg:col-span-12 xl:col-span-12 2xl:col-span-12">
      <label className={label} htmlFor="desc_short">Lyhyt kuvaus (max 1000 merkkiä)</label>

      <textarea
        id="desc_short"
        aria-describedby="desc_short_help desc_short_counter"
        className={`${input} h-28 py-2`}
        name="description_short"
        placeholder="Esim. Moderni bilebändi yritys- ja yksityistilaisuuksiin..."
        value={form.description_short}
        onChange={onChange}
        maxLength={MAX_DESC}
        disabled={disabled}
      />

      <div id="desc_short_counter" className="mt-1 text-right mb-3 flex items-center gap-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-xs md:text-xs opacity-75" aria-live="polite">
        {form.description_short.length}/{MAX_DESC}
      </div>
    </div>

    {/* Väli-viiva */}
    <div className="col-span-12">
      <div
        role="separator"
        aria-hidden="true"
        className="
          w-full max-w-full my-8 h-[2px] rounded-full
          bg-gradient-to-r from-primary/50 via-pink/50 to-electric/50
          dark:from-primary dark:via-pink dark:to-electric
          ring-1 ring-black/5 dark:ring-white/10
        "
      />
    </div>

{/* Musiikkityylit */}
<div className="col-span-12 sm:col-span-12 md:col-span-12 mdd:col-span-12 lg:col-span-12 xl:col-span-12 2xl:col-span-12">

  <label className={label}>Musiikkityylit (max 6)</label>

  {/* avaa/sulje lista */}
  <button
    type="button"
    className={[
      "w-full justify-start",
      pillBase,
      pillGhost,
      "h-9 sm:h-10",
      "text-sm xxxsm:text-sm xxsm:text-sm s24:text-sm xsm:text-sm",
      "sm:text-base md:text-base"
    ].join(" ")}
    onClick={() => setGenreOpen(o => !o)}
    disabled={disabled}
  >
    {genreOpen ? "Sulje lista" : "Valitse tyylejä…"}
  </button>

  {/* listan sisältö */}
  {genreOpen && (
    <div className="mt-1 max-h-60 overflow-auto w-full rounded-lg bg-white dark:bg-black/90 border border-black/10 dark:border-white/10 shadow">
      {allGenres.length ? (
        allGenres.map((g) => {
          const checked = genreIds.some((id) => String(id) === String(g.id));
          const dis = !checked && genreIds.length >= 6;
          return (
            <label
              key={String(g.id)}
              className={`flex items-center gap-2 px-3 py-2 cursor-pointer ${dis ? "opacity-50" : ""}`}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={dis || disabled}
                onChange={(e) => toggleGenre(g.id, e.target.checked)}
              />
              <span>{g.name}</span>
            </label>
          );
        })
      ) : (
        <div className="px-3 py-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-xs md:text-xs opacity-70">
          Ei genrejä (backend ei palauttanut listaa).
        </div>
      )}
    </div>
  )}

  {/* laskuri */}
  <p className="text-xs opacity-70 mt-1">{genreIds.length}/6 valittu</p>

  {/* valitut chippeinä – NAPIN/LISTAN ALLA */}
  <div className="my-4 flex flex-wrap gap-2">
    {genreIds.length ? (
      genreIds.map((id) => {
        const g = allGenres.find((x) => String(x.id) === String(id));
        if (!g) return null;
        return (
          <span key={id} className={[pillBase, pillSelected].join(" ")}>
            <span className="px-0.5">{g.name}</span>
            <button
              type="button"
              aria-label="Poista tyyli"
              onClick={() => removeGenre(id)}
              className={chipCloseBtn}
              disabled={disabled}
            >
              ×
            </button>
          </span>
        );
      })
    ) : (
      <span className="mb-3 flex items-center gap-2 dark:text-white/80 text-black/80 font-customFont1 xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-xs md:text-xs opacity-70">
        Ei valittuja tyylejä.
      </span>
    )}
  </div>
</div>


    {/* Väli-viiva */}
    <div className="col-span-12">
      <div
        role="separator"
        aria-hidden="true"
        className="
          w-full max-w-full my-8 h-[2px] rounded-full
          bg-gradient-to-r from-primary/50 via-pink/50 to-electric/50
          dark:from-primary dark:via-pink dark:to-electric
          ring-1 ring-black/5 dark:ring-white/10
        "
      />
    </div>

{/* Keikkailualueet – sama pill-tyyli kuin Musiikkityylit */}
<div className="col-span-12 sm:col-span-12 md:col-span-12 mdd:col-span-12 lg:col-span-12 xl:col-span-12 2xl:col-span-12">
  <label className={label}>Keikkailualueesi</label>
  {pricedRegions.length ? (
    <div className="flex flex-wrap gap-2 my-4">
      {pricedRegions.map((r) => (
        <span key={r} className={[pillBase, pillActive, "cursor-default"].join(" ")}>
          <span className="px-0.5">{r}</span>
        </span>
      ))}
    </div>
  ) : (
    <p className="dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm opacity-70">
      Ei vielä alueita. Lisää hinnat Hinnoittelu-välilehdellä.
    </p>
  )}
  <div className="mt-2 flex items-center gap-2 flex-wrap">
    <p className="dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm opacity-70">
      Vinkki: esiintymisalueet päivittyvät automaattisesti tähän sen mukaan, mihin maakuntiin olet määrittänyt setit ja hinnat Hinnoittelu-välilehdellä.
    </p>
  </div>
</div>

{/* Väli-viiva */}
<div className="col-span-12">
  <div
    role="separator"
    aria-hidden="true"
    className="
      w-full max-w-full my-8 h-[2px] rounded-full
      bg-gradient-to-r from-primary/50 via-pink/50 to-electric/50
      dark:from-primary dark:via-pink dark:to-electric
      ring-1 ring-black/5 dark:ring-white/10
    "
  />
</div>

{/* Soittimet */}
<div className="col-span-12">
  <label className={label}>Soittimet (max {MAX_INSTRUMENTS})</label>

  {/* avaa/sulje lista */}
  <button
    type="button"
    className={[
      "w-full justify-start",
      pillBase,
      pillGhost,
      "h-9 sm:h-10",
      "text-sm xxxsm:text-sm xxsm:text-sm s24:text-sm xsm:text-sm",
      "sm:text-base md:text-base",
    ].join(" ")}
    onClick={() => setInstrumentsOpen((o) => !o)}
    disabled={disabled || dictLoading}
  >
    {instrumentsOpen ? "Sulje lista" : "Valitse soittimia…"}
  </button>

  {/* listan sisältö */}
  {instrumentsOpen && (
    <div className="mt-1 max-h-60 overflow-auto w-full rounded-lg bg-white dark:bg-black/90 border border-black/10 dark:border-white/10 shadow">
      {dictLoading && (
        <div className="px-3 py-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm opacity-70">
          Ladataan…
        </div>
      )}
      {!dictLoading && dicts.instruments.length === 0 && (
        <div className="px-3 py-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm opacity-70">
          {dictError || "Ei vaihtoehtoja"}
        </div>
      )}

      {dicts.instruments.map((opt) => {
        const id = String(opt.id);
        const checked = instrumentIds.includes(id);
        const dis = !checked && instrumentIds.length >= MAX_INSTRUMENTS;
        return (
          <label
            key={id}
            className={`flex items-center gap-2 px-3 py-2 cursor-pointer ${dis ? "opacity-50" : ""}`}
          >
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={checked}
              disabled={dis || disabled}
              onChange={(e) =>
                toggleId(instrumentIds, setInstrumentIds, id, e.target.checked, MAX_INSTRUMENTS)
              }
            />
            <span>{opt.label ?? opt.name}</span>
          </label>
        );
      })}
    </div>
  )}

  <p className="text-xs opacity-70 mt-1">{instrumentIds.length}/{MAX_INSTRUMENTS} valittu</p>

  {/* valitut chippeinä – ALHAALLA */}
  <div className="mt-3 flex flex-wrap gap-2">
    {instrumentIds.length ? (
      instrumentIds.map((id) => {
        const o = dicts.instruments.find((x) => String(x.id) === String(id));
        return (
          <span key={id} className={[pillBase, pillSelected].join(" ")}>
            <span className="px-0.5">{o?.name ?? o?.label ?? id}</span>
            <button
              type="button"
              aria-label="Poista soitin"
              onClick={() =>
                toggleId(instrumentIds, setInstrumentIds, id, false, MAX_INSTRUMENTS)
              }
              disabled={disabled}
              className={chipCloseBtn}
            >
              ×
            </button>
          </span>
        );
      })
    ) : rawInstrFallback.length ? (
      rawInstrFallback.map((name) => (
        <span key={`raw-${name}`} className={[pillBase, pillGhost, "cursor-default"].join(" ")}>
          <span className="px-0.5">{name}</span>
        </span>
      ))
    ) : (
      <span className="opacity-70">Ei valittuja soittimia.</span>
    )}
  </div>


  {/* Väli-viiva */}
  <div className="col-span-12">
    <div
      role="separator"
      aria-hidden="true"
      className="
        w-full max-w-full my-8 h-[2px] rounded-full
        bg-gradient-to-r from-primary/50 via-pink/50 to-electric/50
        dark:from-primary dark:via-pink dark:to-electric
        ring-1 ring-black/5 dark:ring-white/10
      "
    />
  </div>

  {/* Palvelut */}
  <div className="col-span-12">
    <label className={label}>Muut palvelut (max {MAX_SERVICES})</label>

    <button
      type="button"
      className={[
        "w-full justify-start",
        pillBase,
        pillGhost,
        "h-9 sm:h-10",
        "text-sm xxxsm:text-sm xxsm:text-sm s24:text-sm xsm:text-sm",
        "sm:text-base md:text-base",
      ].join(" ")}
      onClick={() => setServicesOpen((o) => !o)}
      disabled={disabled || dictLoading}
    >
      {servicesOpen ? "Sulje lista" : "Valitse palveluja…"}
    </button>

    {servicesOpen && (
      <div className="mt-1 max-h-60 overflow-auto w-full rounded-lg bg-white dark:bg-black/90 border border-black/10 dark:border-white/10 shadow">
        {dictLoading && <div className="px-3 py-2 text-sm opacity-70">Ladataan…</div>}
        {!dictLoading && dicts.services.length === 0 && (
          <div className="px-3 py-2 text-sm opacity-70">{dictError || "Ei vaihtoehtoja"}</div>
        )}

        {dicts.services.map((opt) => {
          const id = String(opt.id);
          const checked = serviceIds.includes(id);
          const dis = !checked && serviceIds.length >= MAX_SERVICES;
          return (
            <label
              key={id}
              className={`flex items-center gap-2 px-3 py-2 cursor-pointer ${dis ? "opacity-50" : ""}`}
            >
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={checked}
                disabled={dis || disabled}
                onChange={(e) => toggleId(serviceIds, setServiceIds, id, e.target.checked, MAX_SERVICES)}
              />
              <span>{opt.label ?? opt.name}</span>
            </label>
          );
        })}
      </div>
    )}

    <p className="text-xs opacity-70 mt-1">
  {serviceIds.length}/{MAX_SERVICES} valittu
</p>

{/* palveluchipit poistettu pyynnöstä */}


    {/* valitut chippeinä – ALHAALLA */}
    <div className="mt-3 flex flex-wrap gap-2">
      {serviceIds.length ? (
        serviceIds.map((id) => {
          const o = dicts.services.find((x) => String(x.id) === String(id));
          return (
            <span key={id} className={[pillBase, pillSelected].join(" ")}>
              <span className="px-0.5">{o?.name ?? o?.label ?? id}</span>
              <button
                type="button"
                aria-label="Poista palvelu"
                onClick={() => toggleId(serviceIds, setServiceIds, id, false)}
                disabled={disabled}
                className={chipCloseBtn}
              >
                ×
              </button>
            </span>
          );
        })

      
      ) : (
        <span className="opacity-70">Ei valittuja palveluja.</span>
      )}
    </div>

  </div>

  </div>

  </form>
);

}

BasicTab.propTypes = {
  band: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  onSaved: PropTypes.func,
  onProfileChange: PropTypes.func,
  canEdit: PropTypes.bool,
  exposeSave: PropTypes.func,
};
